import React, { Component } from 'react';
import {
    Grid,
    Row,
    Col,
    Table,
    Button,
    FormControl,
    InputGroup,
    Glyphicon,
    Modal,
    Alert,
    FormGroup,
    ControlLabel
} from 'react-bootstrap';
import Card from 'components/Card/Card';
import { firestore } from '../../../config/firebase';

class ProfessionsManagement extends Component {
    constructor(props) {
        super(props);
        this.state = {
            professionals: [],
            searchTerm: '',
            showModal: false,
            selectedProfessional: null,
            isEditing: false,
            showDeleteConfirm: false,
            professionalToDelete: null,
            currentPage: 1,
            itemsPerPage: 20,
            loading: false,
            error: null,
            newProfessional: {
                name: '',
                id: '',
                status: '',
                department: ''
            }
        };
    }

    componentDidMount() {
        this.fetchProfessionals();
    }

    fetchProfessionals = async () => {
        this.setState({ loading: true, error: null });
        try {
            const snapshot = await firestore.collection('professionals')
                .orderBy('name', 'asc')
                .get();

            const professionals = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                // Normaliser les données pour gérer les différents formats de champs
                const normalizedData = {
                    docId: doc.id,
                    name: data.name || data['NOM PRENOMS'] || data.nom || '',
                    id: data.registrationNumber || data.id || data['Nº D\'INSCRIPTION'] || data.numero_enregistrement || data.code || '',
                    status: data.structure || data.status || data['STRUCTURE'] || data.etablissement || data.affiliation || '',
                    department: data.section || data.department || data['SECTION'] || data.type || data.domaine || '',
                    // Conserver les données originales aussi
                    originalData: data
                };
                professionals.push(normalizedData);
            });

            this.setState({ professionals, loading: false });
        } catch (error) {
            this.setState({
                error: `Erreur lors du chargement des professionnels: ${error.message}`,
                loading: false
            });
        }
    };

    handleSearch = (e) => {
        this.setState({ searchTerm: e.target.value, currentPage: 1 });
    };

    getFilteredProfessionals = () => {
        const { professionals, searchTerm } = this.state;
        if (!searchTerm) return professionals;

        return professionals.filter(professional =>
            professional.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            professional.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            professional.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            professional.department?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    getCurrentPageProfessionals = () => {
        const { currentPage, itemsPerPage } = this.state;
        const filtered = this.getFilteredProfessionals();
        const start = (currentPage - 1) * itemsPerPage;
        const end = start + itemsPerPage;
        return filtered.slice(start, end);
    };

    getTotalPages = () => {
        const filtered = this.getFilteredProfessionals();
        return Math.ceil(filtered.length / this.state.itemsPerPage);
    };

    handlePageChange = (pageNumber) => {
        this.setState({ currentPage: pageNumber });
    };

    openModal = (professional = null) => {
        if (professional) {
            this.setState({
                showModal: true,
                isEditing: true,
                selectedProfessional: professional,
                newProfessional: { ...professional }
            });
        } else {
            this.setState({
                showModal: true,
                isEditing: false,
                selectedProfessional: null,
                newProfessional: {
                    name: '',
                    id: '',
                    status: '',
                    department: ''
                }
            });
        }
    };

    closeModal = () => {
        this.setState({
            showModal: false,
            isEditing: false,
            selectedProfessional: null,
            newProfessional: {
                name: '',
                id: '',
                status: '',
                department: ''
            }
        });
    };

    handleInputChange = (field, value) => {
        this.setState({
            newProfessional: {
                ...this.state.newProfessional,
                [field]: value
            }
        });
    };

    saveProfessional = async () => {
        const { newProfessional, isEditing, selectedProfessional } = this.state;

        if (!newProfessional.name.trim()) {
            alert('Le nom du professionnel est requis');
            return;
        }

        if (!newProfessional.id.trim()) {
            alert('L\'ID du professionnel est requis');
            return;
        }

        try {
            // Préparer les données à sauvegarder avec tous les formats possibles
            const dataToSave = {
                // Format principal utilisé par l'importateur
                name: newProfessional.name.trim(),
                registrationNumber: newProfessional.id.trim(),
                structure: newProfessional.status.trim(),
                section: newProfessional.department.trim(),

                // Formats alternatifs pour compatibilité avec l'ancien système
                id: newProfessional.id.trim(),
                status: newProfessional.status.trim(),
                department: newProfessional.department.trim(),

                // Formats originaux pour compatibilité
                'NOM PRENOMS': newProfessional.name.trim(),
                'Nº D\'INSCRIPTION': newProfessional.id.trim(),
                'STRUCTURE': newProfessional.status.trim(),
                'SECTION': newProfessional.department.trim(),

                // Format JSON simplifié
                nom: newProfessional.name.trim(),
                numero_enregistrement: newProfessional.id.trim(),
                etablissement: newProfessional.status.trim(),
                type: newProfessional.department.trim(),

                // Métadonnées
                createdAt: isEditing ? (selectedProfessional?.originalData?.createdAt || new Date()) : new Date(),
                updatedAt: new Date(),
                isActive: true
            };

            if (isEditing && selectedProfessional) {
                await firestore.collection('professionals').doc(selectedProfessional.docId).update(dataToSave);
            } else {
                await firestore.collection('professionals').add(dataToSave);
            }

            this.closeModal();
            this.fetchProfessionals();
        } catch (error) {
            alert('Erreur lors de la sauvegarde: ' + error.message);
        }
    };

    confirmDelete = (professional) => {
        this.setState({
            showDeleteConfirm: true,
            professionalToDelete: professional
        });
    };

    deleteProfessional = async () => {
        const { professionalToDelete } = this.state;
        if (!professionalToDelete) return;

        try {
            await firestore.collection('professionals').doc(professionalToDelete.docId).delete();
            this.setState({ showDeleteConfirm: false, professionalToDelete: null });
            this.fetchProfessionals();
        } catch (error) {
            alert('Erreur lors de la suppression');
        }
    };

    renderProfessionalsTable = () => {
        const professionals = this.getCurrentPageProfessionals();

        return (
            <Table striped bordered condensed hover>
                <thead>
                    <tr>
                        <th>Nom et Prénoms</th>
                        <th>N° d'Inscription</th>
                        <th>Structure/Établissement</th>
                        <th>Section/Type</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {professionals.map((professional) => (
                        <tr key={professional.docId}>
                            <td><strong>{professional.name}</strong></td>
                            <td>{professional.id || '-'}</td>
                            <td>{professional.status || '-'}</td>
                            <td>{professional.department || '-'}</td>
                            <td>
                                <Button
                                    bsSize="xs"
                                    bsStyle="info"
                                    onClick={() => this.openModal(professional)}
                                    style={{ marginRight: '5px' }}
                                >
                                    <Glyphicon glyph="edit" /> Modifier
                                </Button>
                                <Button
                                    bsSize="xs"
                                    bsStyle="danger"
                                    onClick={() => this.confirmDelete(professional)}
                                >
                                    <Glyphicon glyph="trash" /> Supprimer
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    renderPagination = () => {
        const totalPages = this.getTotalPages();
        const { currentPage } = this.state;

        if (totalPages <= 1) return null;

        return (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <div>
                    <Button
                        disabled={currentPage === 1}
                        onClick={() => this.handlePageChange(currentPage - 1)}
                        style={{ marginRight: '10px' }}
                    >
                        Précédent
                    </Button>
                    <span style={{ margin: '0 15px' }}>
                        Page {currentPage} sur {totalPages}
                    </span>
                    <Button
                        disabled={currentPage === totalPages}
                        onClick={() => this.handlePageChange(currentPage + 1)}
                        style={{ marginLeft: '10px' }}
                    >
                        Suivant
                    </Button>
                </div>
            </div>
        );
    };

    render() {

        const {
            loading,
            error,
            showModal,
            isEditing,
            newProfessional,
            showDeleteConfirm,
            professionalToDelete
        } = this.state;

        const totalProfessionals = this.getFilteredProfessionals().length;

        return (
            <div className="content">
                <Grid fluid>
                    <Row>
                        <Col md={12}>
                            <Card
                                content={
                                    <div>
                                        {error && <Alert bsStyle="danger">{error}</Alert>}

                                        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ flex: 1, marginRight: '20px' }}>
                                                <InputGroup>
                                                    <FormControl
                                                        type="text"
                                                        placeholder="Rechercher un professionnel..."
                                                        value={this.state.searchTerm}
                                                        onChange={this.handleSearch}
                                                    />
                                                    <InputGroup.Addon>
                                                        <Glyphicon glyph="search" />
                                                    </InputGroup.Addon>
                                                </InputGroup>
                                            </div>
                                            <div>
                                                <Button
                                                    bsStyle="success"
                                                    onClick={() => this.openModal()}
                                                >
                                                    <Glyphicon glyph="plus" /> Ajouter un Professionnel
                                                </Button>
                                            </div>
                                        </div>

                                        <div style={{ marginBottom: '15px' }}>
                                            <strong>Total: {totalProfessionals} professionnel(s)</strong>
                                        </div>

                                        {loading ? (
                                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                                Chargement des professionnels...
                                            </div>
                                        ) : (
                                            <>
                                                {this.renderProfessionalsTable()}
                                                {this.renderPagination()}
                                            </>
                                        )}
                                    </div>
                                }
                            />
                        </Col>
                    </Row>
                </Grid>

                {/* Modal d'ajout/modification */}
                <Modal show={showModal} onHide={this.closeModal} bsSize="large">
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {isEditing ? 'Modifier le Professionnel' : 'Ajouter un Professionnel'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <form>
                            <FormGroup>
                                <ControlLabel>Nom du professionnel *</ControlLabel>
                                <FormControl
                                    type="text"
                                    value={newProfessional.name}
                                    onChange={(e) => this.handleInputChange('name', e.target.value)}
                                    placeholder="Ex: TRAORE RASMATA EP. OUEDRAOGO, AHOUDJI FURAX"
                                />
                            </FormGroup>

                            <FormGroup>
                                <ControlLabel>Numéro d'inscription *</ControlLabel>
                                <FormControl
                                    type="text"
                                    value={newProfessional.id}
                                    onChange={(e) => this.handleInputChange('id', e.target.value)}
                                    placeholder="Ex: R1-0168D, R1-0840A, R2-1577A"
                                />
                            </FormGroup>

                            <FormGroup>
                                <ControlLabel>Structure/Établissement</ControlLabel>
                                <FormControl
                                    type="text"
                                    value={newProfessional.status}
                                    onChange={(e) => this.handleInputChange('status', e.target.value)}
                                    placeholder="Ex: CHR KOUDOUGOU, KOMKI IPALA, BLESSING"
                                />
                            </FormGroup>

                            <FormGroup>
                                <ControlLabel>Section/Type</ControlLabel>
                                <FormControl
                                    componentClass="select"
                                    value={newProfessional.department}
                                    onChange={(e) => this.handleInputChange('department', e.target.value)}
                                >
                                    <option value="">Sélectionner une section</option>
                                    <option value="Officine">Officine</option>
                                    <option value="Etablissement de préparation et de distribution en gros">Etablissement de préparation et de distribution en gros</option>
                                    <option value="Administration, Structure de formation et de recherche en santé">Administration, Structure de formation et de recherche en santé</option>
                                    <option value="Laboratoire de biologie médicale">Laboratoire de biologie médicale</option>
                                </FormControl>
                                <FormControl
                                    type="text"
                                    value={newProfessional.department}
                                    onChange={(e) => this.handleInputChange('department', e.target.value)}
                                    placeholder="Ou saisir manuellement: Officine, Laboratoire de biologie médicale..."
                                    style={{ marginTop: '5px' }}
                                />
                            </FormGroup>
                        </form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={this.closeModal}>Annuler</Button>
                        <Button bsStyle="primary" onClick={this.saveProfessional}>
                            {isEditing ? 'Modifier' : 'Ajouter'}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal de confirmation de suppression */}
                <Modal show={showDeleteConfirm} onHide={() => this.setState({ showDeleteConfirm: false })}>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirmer la suppression</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Êtes-vous sûr de vouloir supprimer le professionnel{' '}
                        <strong>{professionalToDelete && professionalToDelete.name}</strong> ?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={() => this.setState({ showDeleteConfirm: false })}>
                            Annuler
                        </Button>
                        <Button bsStyle="danger" onClick={this.deleteProfessional}>
                            Supprimer
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}

export default ProfessionsManagement;
